/**
 * 统一的批注文本渲染函数
 * 用于在原文中标记错误，显示删除线和修改建议
 */

export interface ErrorAnnotation {
  error_text: string;
  suggestion: string;
  error_type: string;
  severity?: string;
}

interface RenderOptions {
  showSuggestion?: boolean;  // 是否显示修改建议
  showTooltip?: boolean;     // 是否显示悬停提示
}

// 错误类型中文映射
const ERROR_TYPE_MAP: { [key: string]: string } = {
  grammar: '语法错误',
  spelling: '拼写错误',
  vocabulary: '词汇使用',
  structure: '结构问题',
  punctuation: '标点符号'
};

/**
 * 渲染带批注的文本
 * @param text 原始文本
 * @param errors 错误列表
 * @param options 渲染选项
 * @returns HTML字符串
 */
export function renderAnnotatedText(
  text: string,
  errors: ErrorAnnotation[],
  options: RenderOptions = {}
): string {
  const { showSuggestion = true, showTooltip = true } = options;
  
  if (!text) return '';
  if (!errors || errors.length === 0) {
    // 没有错误时，返回转义后的纯文本
    return escapeHtml(text);
  }
  
  // 创建替换映射
  const replacements = new Map<string, { original: string; replacement: string }>();
  
  // 按长度排序，先处理长的错误文本
  const sortedErrors = [...errors].sort((a, b) => {
    const lenA = a.error_text?.length || 0;
    const lenB = b.error_text?.length || 0;
    return lenB - lenA;
  });
  
  // 收集所有需要替换的文本
  sortedErrors.forEach((error) => {
    const errorText = error.error_text;
    if (errorText && errorText.trim() && !replacements.has(errorText)) {
      // 构建替换HTML
      let replacement = '';
      
      if (showTooltip) {
        const errorTypeText = ERROR_TYPE_MAP[error.error_type] || error.error_type;
        const tooltipText = `${errorTypeText}: ${error.suggestion || '无建议'}`;
        
        replacement = `<span class="error-wrapper error-${error.error_type}" data-tooltip="${escapeHtml(tooltipText)}">`;
        replacement += `<span class="error-mark">${escapeHtml(errorText)}</span>`;
        
        if (showSuggestion && error.suggestion) {
          replacement += `<span class="correction-suggestion">${escapeHtml(error.suggestion)}</span>`;
        }
        
        replacement += `</span>`;
      } else {
        // 简单模式，只显示删除线
        replacement = `<span class="error-mark error-${error.error_type}">${escapeHtml(errorText)}</span>`;
        
        if (showSuggestion && error.suggestion) {
          replacement += `<span class="correction-suggestion">${escapeHtml(error.suggestion)}</span>`;
        }
      }
      
      replacements.set(errorText, { original: errorText, replacement });
    }
  });
  
  // 转义HTML并执行替换
  let markedText = escapeHtml(text);
  
  replacements.forEach(({ original, replacement }) => {
    // 创建安全的正则表达式，支持大小写不敏感匹配
    const escapedOriginal = escapeRegExp(original);
    const regex = new RegExp(`\\b${escapedOriginal}\\b`, 'gi');
    markedText = markedText.replace(regex, replacement);
  });
  
  return markedText;
}

/**
 * HTML转义函数
 */
function escapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}

/**
 * 正则表达式特殊字符转义
 */
function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * React组件版本 - 可选使用
 */
export function AnnotatedText({ 
  text, 
  errors, 
  options = {} 
}: {
  text: string;
  errors: ErrorAnnotation[];
  options?: RenderOptions;
}) {
  const html = renderAnnotatedText(text, errors, options);
  return <div 
    className="annotated-text-display"
    dangerouslySetInnerHTML={{ __html: html }}
  />;
}